<template>
  <div>
    <div v-if="loadingQuote" class="message-wrapper">
      <loader v-if="!showConfigurationCodePrompt"></loader>
    </div>
    <div v-else-if="!loadingQuote && hasError" class="message-wrapper">
      <div>
        <p>Kon offerte niet laden</p>
        <p>
          <router-link
            :to="{
              name: 'quote-overview-page',
              cstType: $route.params.cstType,
            }"
          >
            <icon icon="angle-left" /> {{ i18n.t('/QuotePage/BackButton') }}
          </router-link>
        </p>
      </div>
    </div>
    <div v-else>
      <!-- For now, redirect only -->
      <b2b-customer-configuration-page
        v-if="$route.params.cstType == 'b2b'"
        :configuration-code="configurationCode"
      ></b2b-customer-configuration-page>
      <b2c-customer-configuration-page
        v-else
        :configuration-code="$route.params.configurationCode"
      ></b2c-customer-configuration-page>
    </div>
    <configuration-code-prompt
      v-if="showConfigurationCodePrompt"
      :code="configurationCode"
      :is-loading="loadingCode"
      @submit="retryConfiguration"
      @close="showConfigurationCodePrompt = false"
    />
    <overwrite-accessories-modal
      v-if="showOverwriteAccessoriesModal"
      :is-loading="loadingCode"
      @overwrite-accessories="updateConfigWithAccessories"
    />
    <expected-delivery-date-prompt
      :show="showExpectedDeliveryDatePrompt"
      @confirm="closeExpectedDeliveryPrompt"
      @close="closeExpectedDeliveryPrompt"
    />
    <modal v-if="showKomNumberModal" title="Info" @close="showKomNumberModal = false">
      <p>{{ i18n.t('/Configuration/ConfigurationInvalid') }} {{ newKomNumber }}</p>
      <app-button data-qa="close-komnumber-modal" @click="showKomNumberModal = false">
        Close
      </app-button>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import quoteOrderService from '@/services/quoteOrderService'
import stockCarsService from '@/services/stockCarsService'
import prefillService from '@/services/prefillService'

import QuoteApi from 'api-client/Quote.api'

import b2cCustomerConfigurationPage from '@/pages/business-to-customer/CustomerConfigurationPage.vue'
import b2bCustomerConfigurationPage from '@/pages/business-to-business/CustomerConfigurationPage.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'

import ConfigurationCodePrompt from '@/components/generic/presentational/organisms/configurationCodePrompt/ConfigurationCodePrompt.vue'
import OverwriteAccessoriesModal from '@/components/generic/presentational/organisms/overwriteAccessoriesModal/OverwriteAccessoriesModal.vue'
import ConfigurationHelper from '@/helpers/ConfigurationHelper'
import ConfigurationApi from '@/api/server/Configuration'
import { EventBus } from '@/plugins/event-bus'
import loadAccessoriesAction from '@/helpers/enums/loadAccessoriesAction'
import CheckExpectedDeliveryDate from '@/mixins/checkExpectedDeliveryDate'
import ExpectedDeliveryDatePrompt from '@/components/generic/presentational/molecules/expectedDeliveryDatePrompt/ExpectedDeliveryDatePrompt.vue'

export default {
  components: {
    Modal,
    AppButton,
    b2bCustomerConfigurationPage,
    b2cCustomerConfigurationPage,
    Loader,
    ConfigurationCodePrompt,
    ExpectedDeliveryDatePrompt,
    OverwriteAccessoriesModal,
    Icon,
  },
  mixins: [CheckExpectedDeliveryDate],
  props: {
    checkExpectedDeliveryDate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      newKomNumber: '',
      showKomNumberModal: false,
      loadingQuote: true,
      loadingSave: false,
      quote: null,
      orderService: quoteOrderService,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      hasError: false,
      showConfigurationCodePrompt: false,
      showOverwriteAccessoriesModal: false,
      configurationCode: null,
      cartId: null,
      loadingCode: false,
      newConfiguration: null,
      isQuoteNew: false,
    }
  },
  computed: {
    ...mapState('quote', {
      shouldCreateMatchingStockQuote: 'shouldCreateMatchingStockQuote',
      createMatchingStockQuoteOptions: 'createMatchingStockQuoteOptions',
      prevQuote: 'prevQuote',
    }),
    ...mapState('user', ['dealerSettings']),
    ...mapGetters({
      expectedDeliveryDates: 'quote/expectedDeliveryDates',
      deliveryDate: 'quote/deliveryDate',
      bpmCalculated: 'quote/bpmCalculated',
      stateCocaId: 'configuration/cocaId',
      quoteIsSalesOrder: 'quote/quoteStatusIsSalesOrder',
    }),
    isStockCar() {
      return this.$store.getters['quote/isStockCar']
    },
    establishmentId() {
      return this.$route.params.establishmentId
    },
    quoteId() {
      return this.$route.params.quoteId
    },
    komnummer() {
      return this.$route.params.komnummer
    },
    stockCarId() {
      return this.$route.query.stockcarid || this.$route.params.stockCarId
    },
    commissionNumber() {
      return this.$route.params.commissionNumber
    },
    commissionYear() {
      return this.$route.params.commissionYear
    },
  },
  watch: {
    $route(to, from) {
      const fromQuoteId = from?.params?.quoteId
      if (
        (!from?.params?.stockCarId && !fromQuoteId) ||
        from?.params?.quoteId === to?.params?.quoteId
      ) {
        return
      }
      this.initPage(true)
    },
  },
  created() {
    EventBus.$on('configuration-change', this.checkConfigurationChange)
    EventBus.$on('kom-number-changed', this.checkKomNumberChange)
  },
  async mounted() {
    await this.initPage()
    EventBus.$on('delivery-dates-loaded', this.onDeliveryDatesLoaded)
  },
  unmounted() {
    // remove all data from the state
    this.initQuote()
    this.$store.dispatch('configuration/resetConfiguration')
    this.$store.dispatch('accessories/reset')
    EventBus.$off('configuration-change')
    EventBus.$off('kom-number-changed')
    EventBus.$off('delivery-dates-loaded', this.onDeliveryDatesLoaded)
  },
  methods: {
    ...mapMutations({
      setShouldCreateMatchingStockQuote: 'quote/setShouldCreateMatchingStockQuote',
      setPrevQuote: 'quote/setPrevQuote',
      setCreateMatchingStockQuoteOptions: 'quote/setCreateMatchingStockQuoteOptions',
    }),
    ...mapActions({
      setLoading: 'quote/setLoading',
    }),
    async initPage(isDuplicate) {
      this.configurationCode =
        this.$route.query.configuratie ||
        this.$route.params.configurationCode ||
        this.$route.query.code ||
        this.$route.query.audicode ||
        this.$route.query.vehiclecode

      this.cartId = this.$route.query.cartId || this.$route.params.cartId

      if (this.quoteId) {
        this.isQuoteNew = false
        // open existing quote
        QuoteApi.getQuoteById(this.$route.params.quoteId)
          .then(async (resp) => {
            this.quote = resp
            this.$store.dispatch('quote/loadQuote', this.quote).then(async () => {
              await this.afterLoadQuote(isDuplicate)
            })

            // set all data from querystring onto the quote
            await prefillService.prefill(this.$route.params, this.$route.query, this.dealerSettings)

            if (
              this.configurationCode ||
              (Object.keys(this.$route.query)?.length && !this.$route.query.new)
            ) {
              this.updateRoute()
            }
            this.loadingQuote = false
          })
          .catch((e) => {
            this.loadingQuote = false
            this.hasError = true
          })
      } else {
        // new quote
        if (this.configurationCode) {
          // new quote with configurationcode
          this.initQuote()
          await this.initNewConfigurationQuote(this.configurationCode)
        } else if (this.stockCarId) {
          await this.initNewStockcarQuote(this.stockCarId)
        } else if (this.komnummer) {
          if (this.dealerSettings.newStockFlowEnabled) {
            await this.createNewStockCarQuote(this.komnummer, null)
          } else {
            // get stockCarId from komnummer
            const stockCarId = await stockCarsService.getStockCarIdFromCommissionNumber(
              this.komnummer
            )
            if (!stockCarId) {
              this.goBack()
            } else {
              await this.initNewStockcarQuote(stockCarId)
            }
          }
        } else if (this.cartId) {
          await this.initNewCartQuote(this.cartId)
        } else if (this.commissionNumber) {
          await this.createNewStockCarQuote(this.commissionNumber, this.commissionYear)
        } else {
          // new quote with configurationcode
          this.initQuote()
          this.showConfigurationCodePrompt = true
        }
      }
    },
    async afterLoadQuote(isDuplicate) {
      let quoteCar = this.$store.getters['quote/car']

      if (quoteCar.Type === 'CONFIGURATION') {
        this.$store
          .dispatch('configuration/getConfiguration', {
            configurationCode: quoteCar.ConfigurationCode,
            deliveryDate: this.deliveryDate,
            calculateBpm:
              this.quote.QuoteDetails.BpmCalculated == null
                ? true
                : this.quote.QuoteDetails.BpmCalculated,
          })
          .then(() => {
            if (isDuplicate) {
              if (
                !this.quote.LatestVwpfsPrivateLeaseCall ||
                !this.quote.LatestVwpfsPrivateLeaseCall?.response?.pricingData?.calculatedProduct
                  ?.rate?.amount
              ) {
                // In case latestVwpfsPrivateLeaseCall is not initialised yet
                EventBus.$emit('set-private-lease-data')
              }
              if (
                !this.quote.LatestVwpfsOperationalLeaseCall ||
                !this.quote.LatestVwpfsOperationalLeaseCall?.response?.pricingData
                  ?.calculatedProduct?.rate?.amount
              ) {
                // In case latestVwpfsPrivateLeaseCall is not initialised yet
                EventBus.$emit('set-operational-lease-data')
              }
              if (
                !this.quote.LatestVwpfsInsuranceCall ||
                !this.quote.LatestVwpfsInsuranceCall?.response
              ) {
                // In case latestVwpfsInsuranceCall is not initialised yet
                EventBus.$emit('set-insurance-data')
              }
            } else {
              if (!this.configurationCode) {
                EventBus.$emit('configuration-loaded')
              } else {
                // update configurationCode (from querystring)
                this.checkConfigurationChange(this.configurationCode).then(() => {
                  EventBus.$emit('configuration-loaded')
                })
              }
            }
          })

        this.$store.dispatch('accessories/getForConfigurationCode', {
          configurationCode: quoteCar.ConfigurationCode,
        })

        this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForConfigurationCode', {
          configurationCode: quoteCar.ConfigurationCode,
          deliveryDate: this.deliveryDate,
        })
      } else if (quoteCar.Type === 'STOCKCAR') {
        await this.$store.dispatch('configuration/getStockcar', {
          stockCarId: quoteCar.StockCarId,
          configurationId: quoteCar.ConfigurationId,
          type: quoteCar.Type,
          deliveryDate: this.deliveryDate,
          calculateBpm:
            this.quote.QuoteDetails.BpmCalculated == null
              ? true
              : this.quote.QuoteDetails.BpmCalculated,
        })

        EventBus.$emit('configuration-loaded')

        this.$store.dispatch('accessories/getForStockCar', {
          stockCarId: quoteCar.StockCarId,
        })

        this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForStockCar', {
          stockCarId: quoteCar.StockCarId,
          deliveryDate: this.deliveryDate,
        })
      } else if (quoteCar.Type === 'STOCKCAR_V2') {
        await this.$store.dispatch('configuration/getNewStock', {
          stockId: quoteCar.StockId,
          configurationId: quoteCar.ConfigurationId,
          deliveryDate: this.deliveryDate,
          calculateBpm:
            this.quote.QuoteDetails.BpmCalculated == null
              ? true
              : this.quote.QuoteDetails.BpmCalculated,
        })

        EventBus.$emit('configuration-loaded')

        this.$store.dispatch('accessories/getForStock', {
          stockId: quoteCar.StockId,
        })

        await this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForStock', {
          stockId: quoteCar.StockId,
          deliveryDate: this.deliveryDate,
        })
      }
    },
    async initNewCartQuote(cartId) {
      this.initQuote()
      try {
        await quoteOrderService.initShoppingCartQuote(cartId)
        await this.$store.dispatch('quote/createFromCartId', cartId)
        this.quote = this.$store.getters['quote/get']
        await this.afterLoadQuote()
        EventBus.$emit('configuration-loaded')
        this.updateRoute(true)
        this.setShouldCreateMatchingStockQuote(false)
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingQuote = false
        this.setLoading(false)
      }
    },
    async createNewStockCarQuote(commissionNumber, commissionYear) {
      this.initQuote()
      try {
        await this.$store.dispatch('quote/createForNewStockCart', {
          commissionNumber: commissionNumber,
          commissionYear: commissionYear,
        })

        await quoteOrderService.setNewStockCarDefaultsOnQuote()

        let quoteCar = this.$store.getters['quote/car']

        await this.$store.dispatch('accessories/initForStock', {
          stockId: quoteCar.StockId,
        })

        this.$store.dispatch('controlCodes/initQuoteOrderControlCodesForStock', {
          stockId: quoteCar.StockId,
          deliveryDate: this.deliveryDate,
        })

        // save control codes + dealer accessories to quote
        // expected to be removed in the future
        // when accessories and control codes are assigned by BE
        await this.$store.dispatch('quote/update')

        this.quote = this.$store.getters['quote/get']

        // async afterLoadQuote is loaded in separate thread simultaneously with this.loadingQuote = false
        this.afterLoadQuote()
        this.updateRoute(true)
        this.setShouldCreateMatchingStockQuote(false)
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingQuote = false
        this.setLoading(false)
      }
    },
    initQuote() {
      let emptyQuote = {
        QuoteDetails: {
          Cars: [],
          DealerAccessories: [],
          ExchangeCars: [],
          ControlCodes: [],
          LeaseReference: '',
          FleetSalesContract: {
            ContractNumber: null,
            DiscountPercentage: null,
            Remarks: null,
            OrderDate: null,
            SelectedDate: null,
          },
          DeliveryDate: null,
          IsStockOrder: false,
          Customer: null,
          CustomerCompany: null,
          Dealer: {
            EstablishmentID: this.$route.params.establishmentId,
          },
          CreatedBySource: this.$route.params.cstType?.toUpperCase(),
          DeliverAsSoonAsPossible: true,
        },
      }

      let customer = {}
      this.$store.dispatch('quote/setQuote', emptyQuote)
      this.$store.dispatch('quote/setCustomer', customer)
    },
    onDeliveryDatesLoaded() {
      if (this.$route.query.new) {
        this.expectedDeliveryDateCheck(true)
        this.updateRoute()
      } else if (!this.quoteIsSalesOrder) {
        this.expectedDeliveryDateCheck(true)
      }
    },
    closeExpectedDeliveryPrompt() {
      this.showExpectedDeliveryDatePrompt = false
    },
    async initNewConfigurationQuote(configurationCode, updateUrl = false) {
      if (!ConfigurationHelper.isValidConfigurationCode(configurationCode)) {
        EventBus.$emit('notification-warning', 'Vul een juiste configuratiecode in')
        return
      }
      if (
        !this.configurationCode ||
        this.configurationCode.toLowerCase() !== configurationCode.toLowerCase()
      ) {
        this.configurationCode = configurationCode
      }
      this.loadingCode = true
      const configuration = await ConfigurationApi.getConfiguration(configurationCode)
        .catch(() => {
          return null
        })
        .finally(() => {
          this.loadingCode = false
        })
      if (!configuration) {
        this.showConfigurationCodePrompt = true
        return
      } else {
        if (this.showConfigurationCodePrompt) {
          this.showConfigurationCodePrompt = false
        }
      }
      await this.$store.dispatch('accessories/initForConfigurationCode', {
        configurationCode,
      })
      try {
        await this.orderService.initConfigurationCarQuote(configurationCode)
        await this.$store.dispatch('quote/create')

        // set all data from querystring onto the quote
        const newRoute = updateUrl
          ? ConfigurationHelper.generateConfigurationRoute(
              this.$route.params,
              this.$route.query,
              configurationCode
            )
          : {
              params: this.$route.params,
              query: this.$route.query,
            }

        await prefillService.prefill(newRoute.params, newRoute.query, this.dealerSettings)

        this.updateRoute(true)

        this.$store.dispatch('quote/update').then(async () => {
          EventBus.$emit('configuration-loaded')
        })
      } catch (error) {
        this.hasError = true
        EventBus.$emit('notification-warning', this.i18n.t('/Quote/CreateQuoteFailed'))
      } finally {
        this.loadingQuote = false
      }
    },
    async initNewStockcarQuote(stockCarId) {
      this.initQuote()
      // new quote with stockcar
      try {
        await this.orderService.initStockCarQuote(stockCarId)
        await this.$store.dispatch('quote/create')
        await prefillService.prefill(this.$route.params, this.$route.query, this.dealerSettings)

        if (this.shouldCreateMatchingStockQuote && this.prevQuote) {
          const newQuote = this.$store.state.quote?.quote

          newQuote.QuoteDetails.Customer = this.prevQuote.QuoteDetails.Customer
          newQuote.QuoteDetails.DeliveryDate = this.prevQuote.QuoteDetails.DeliveryDate
          newQuote.QuoteDetails.DoNotDeliverBeforeDate =
            this.prevQuote.QuoteDetails.DoNotDeliverBeforeDate
          newQuote.QuoteDetails.StockButtonDate = this.prevQuote.QuoteDetails.StockButtonDate
          newQuote.QuoteDetails.PlQuotationId = this.prevQuote.QuoteDetails.PlQuotationId
          newQuote.QuoteDetails.DeliverAsSoonAsPossible =
            this.prevQuote.QuoteDetails.DeliverAsSoonAsPossible
          newQuote.QuoteDetails.BpmCalculated = this.prevQuote.QuoteDetails.BpmCalculated
          newQuote.QuoteDetails.IsStockOrder = this.prevQuote.QuoteDetails.IsStockOrder
          newQuote.QuoteDetails.LeaseCompany = this.prevQuote.QuoteDetails.LeaseCompany
          newQuote.QuoteDetails.PrintOptions = this.prevQuote.QuoteDetails.PrintOptions
          newQuote.QuoteDetails.CustomerCompany = this.prevQuote.QuoteDetails.CustomerCompany
          newQuote.QuoteDetails.CustomerType = this.prevQuote.QuoteDetails.CustomerType
          newQuote.QuoteDetails.LeaseReference = this.prevQuote.QuoteDetails.LeaseReference
          newQuote.QuoteDetails.FleetSalesContract = this.prevQuote.QuoteDetails.FleetSalesContract
          newQuote.QuoteDetails.Remark = this.prevQuote.QuoteDetails.Remark

          if (this.createMatchingStockQuoteOptions?.isCopyExchangeCar) {
            newQuote.QuoteDetails.ExchangeCars = this.prevQuote.QuoteDetails.ExchangeCars
          }

          if (this.createMatchingStockQuoteOptions?.isCopyAccessories) {
            newQuote.QuoteDetails.DealerAccessories = this.replaceQuotesOptions(
              newQuote.QuoteDetails.DealerAccessories,
              this.prevQuote.QuoteDetails.DealerAccessories,
              'DealerAccessories'
            )
          }

          if (this.createMatchingStockQuoteOptions?.isCopyDeliveryCosts) {
            newQuote.QuoteDetails.DealerAccessories = this.replaceQuotesOptions(
              newQuote.QuoteDetails.DealerAccessories,
              this.prevQuote.QuoteDetails.DealerAccessories,
              'DeliveryCosts'
            )
          }

          if (this.createMatchingStockQuoteOptions?.isCopyDiscount) {
            newQuote.QuoteDetails.DealerAccessories = this.replaceQuotesOptions(
              newQuote.QuoteDetails.DealerAccessories,
              this.prevQuote.QuoteDetails.DealerAccessories,
              'Corrections'
            )
          }

          if (this.createMatchingStockQuoteOptions?.isCopyNonCoverableCosts) {
            newQuote.QuoteDetails.DealerAccessories = this.replaceQuotesOptions(
              newQuote.QuoteDetails.DealerAccessories,
              this.prevQuote.QuoteDetails.DealerAccessories,
              'NonRecoverableCosts'
            )
          }

          this.$store.dispatch('quote/setQuote', newQuote)
          this.setCreateMatchingStockQuoteOptions(null)
          this.setPrevQuote(null)
        }

        await this.$store.dispatch('quote/update')
        EventBus.$emit('configuration-loaded')
        this.updateRoute(true)
        this.setShouldCreateMatchingStockQuote(false)
        this.setLoading(false)
      } catch (error) {
        console.log(error)
      }
    },
    replaceQuotesOptions(newAccessories, oldAccessories, optionCategoryName) {
      let otherOptions =
        newAccessories?.filter((option) => option.Category !== optionCategoryName) || []

      let copiedOptions =
        oldAccessories?.filter((option) => option.Category === optionCategoryName) || []

      return otherOptions.concat(copiedOptions)
    },
    async checkConfigurationChange(configurationCode) {
      // Check CocaId before updating
      if (this.configurationCode !== configurationCode) {
        this.configurationCode = configurationCode
      }
      const newConfiguration = await ConfigurationApi.getConfiguration(
        configurationCode,
        this.deliveryDate,
        this.bpmCalculated
      )
      this.newConfiguration = newConfiguration

      if (newConfiguration.CocaId !== this.stateCocaId) {
        // Trimline or modal change
        this.showOverwriteAccessoriesModal = true
      } else {
        this.orderService.updateConfiguration(
          configurationCode,
          this.deliveryDate,
          loadAccessoriesAction.keep, // Always keep existing accessories and prices when cocaId doesn't change
          newConfiguration,
          true,
          this.bpmCalculated
        )
      }
    },
    async checkKomNumberChange(komNumber) {
      this.$store.dispatch('configuration/setIsKomNumberUpdating', true)

      let quoteCar = this.$store.getters['quote/car']

      if (quoteCar.Type === 'STOCKCAR') {
        const stockCarIdFromKomNumber =
          await stockCarsService.getStockCarIdFromCommissionNumber(komNumber)

        if (!stockCarIdFromKomNumber) {
          this.newKomNumber = komNumber
          this.showKomNumberModal = true
          this.$store.dispatch('configuration/setIsKomNumberUpdating', false)
          return
        }
        await this.orderService.updateStockCar(stockCarIdFromKomNumber, this.deliveryDate)
      }

      if (quoteCar.Type === 'STOCKCAR_V2') {
        const stockIdFromKomNumber =
          await stockCarsService.getStockIdFromCommissionNumber(komNumber)

        if (!stockIdFromKomNumber) {
          this.newKomNumber = komNumber
          this.showKomNumberModal = true
          this.$store.dispatch('configuration/setIsKomNumberUpdating', false)
          return
        }

        await this.orderService.updateStock(stockIdFromKomNumber, this.deliveryDate, true)
      }

      this.$store.dispatch('configuration/setIsKomNumberUpdating', false)
    },
    updateConfigWithAccessories(overwriteAccessories) {
      this.loadingCode = true
      this.orderService
        .updateConfiguration(
          this.configurationCode,
          this.deliveryDate,
          overwriteAccessories ? loadAccessoriesAction.overwrite : loadAccessoriesAction.renew,
          this.newConfiguration,
          true,
          this.bpmCalculated
        )
        .then(() => {
          this.newConfiguration = null
          this.showOverwriteAccessoriesModal = false
        })
        .finally(() => {
          this.loadingCode = false
        })
    },
    goBack() {
      if (this.$route.query.returnUrl) {
        this.$router.push(this.$route.query.returnUrl)
      } else {
        this.$router.push({
          name: 'quote-overview-page',
          params: {
            cstType: this.$route.params.cstType,
            establishmentId: this.$route.params.establishmentId,
          },
          force: true,
        })
      }
    },
    updateRoute(isNew = null) {
      let params = {
        cstType: this.$route.params.cstType,
        establishmentId: this.$route.params.establishmentId,
        quoteId: this.$store.getters['quote/quoteId'],
      }

      // Preserve existing query parameters (like mode=approval)
      let query = { ...this.$route.query }
      if (isNew) {
        query.new = isNew
      }

      this.$router.replace({
        name: 'configuration-page',
        params,
        query: Object.keys(query).length > 0 ? query : null,
      })
    },
    retryConfiguration(configurationCode) {
      this.initNewConfigurationQuote(configurationCode, true)
    },
  },
}
</script>

<style lang="scss" scoped>
.message-wrapper {
  display: flex;
  align-content: center;
  align-items: center;
  height: 100vh;

  div {
    margin: auto;
  }
}

:deep(.label--mandatory) {
  color: $color-danger;
}
</style>
